import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ROUTE_PATHS } from "../config/routes";
import { PermissionLike, getPermissionKeys } from "../utils/permissions";

/**
 * Pure utility – resolves the correct dashboard path for a given permissions array.
 * Use this when you already have the user/permissions at hand (e.g. right after login).
 */
export function getDashboardPath(permissions: PermissionLike[]): string {
  const permissionKeys = getPermissionKeys(permissions);

  if (permissionKeys.length > 0 && !permissionKeys.includes("home:view")) {
    return `/dashboard/${permissionKeys[0].split(":")[0]}`;
  }
  return ROUTE_PATHS.DASHBOARD.HOME;
}

/**
 * Hook – returns a ready-to-call navigate function.
 * - Unauthenticated  → /omidex
 * - Authenticated    → best dashboard path based on user permissions
 *
 * Use this anywhere you need a "go to dashboard" CTA without
 * duplicating permission-aware routing logic.
 *
 * @example
 *   const goToDashboard = useDashboardNavigate();
 *   <Button onClick={goToDashboard}>داشبورد</Button>
 */
export function useDashboardNavigate(): () => void {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  return () => {
    if (!isAuthenticated) {
      navigate(ROUTE_PATHS.LOGIN);
      return;
    }
    navigate(getDashboardPath(user?.role?.permissions ?? []));
  };
}
